/**
 * useCamera Hook
 * Custom hook for camera capture functionality
 */

import { useState, useCallback, useRef } from 'react'
import Webcam from 'react-webcam'

interface CameraState {
  photoData: string | null
  isCapturing: boolean
  error: string | null
  isCameraReady: boolean
}

interface UseCameraReturn extends CameraState {
  webcamRef: React.RefObject<Webcam | null>
  capturePhoto: () => string | null
  retakePhoto: () => void
  clearError: () => void
  setCameraReady: (ready: boolean) => void
}

export function useCamera(): UseCameraReturn {
  const webcamRef = useRef<Webcam | null>(null)
  const [state, setState] = useState<CameraState>({
    photoData: null,
    isCapturing: false,
    error: null,
    isCameraReady: false,
  })

  const capturePhoto = useCallback((): string | null => {
    if (!webcamRef.current) {
      setState(prev => ({
        ...prev,
        error: 'Camera not initialized',
      }))
      return null
    }

    setState(prev => ({ ...prev, isCapturing: true, error: null }))

    try {
      const imageSrc = webcamRef.current.getScreenshot()
      if (imageSrc) {
        setState({
          photoData: imageSrc,
          isCapturing: false,
          error: null,
          isCameraReady: true,
        })
        return imageSrc
      } else {
        setState(prev => ({
          ...prev,
          isCapturing: false,
          error: 'Failed to capture photo',
        }))
        return null
      }
    } catch (err) {
      console.error('Error capturing photo:', err)
      setState(prev => ({
        ...prev,
        isCapturing: false,
        error: 'Error capturing photo',
      }))
      return null
    }
  }, [])

  const retakePhoto = useCallback(() => {
    setState(prev => ({
      ...prev,
      photoData: null,
      error: null,
    }))
  }, [])

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  const setCameraReady = useCallback((ready: boolean) => {
    setState(prev => ({ ...prev, isCameraReady: ready }))
  }, [])

  return {
    ...state,
    webcamRef,
    capturePhoto,
    retakePhoto,
    clearError,
    setCameraReady,
  }
}

// Video constraints for mobile-friendly camera
export const videoConstraints = {
  width: 720,
  height: 720,
  facingMode: 'environment', // Use back camera on mobile
}
